import { z } from 'zod';
/**
 * Admin authentication contract.
 *
 * Sign-in is email and password against `AdminCredential`, separate from the
 * consumer Firebase phone flow. What a signed-in operator may *do* still comes
 * from `User.roles` — the capability helpers at the bottom are the single place
 * that decides, so the API and the console cannot disagree about it.
 */
/** Every role the API can report. */
export const USER_ROLES = ['user', 'admin', 'support'];
export const userRoleSchema = z.enum(USER_ROLES);
/** The roles that may sign in to the console at all. */
export const ADMIN_ROLES = ['admin', 'support'];
export const adminLoginRequestSchema = z.object({
    // Normalised here so the API and the console agree on what "the same email"
    // means without either having to remember to lower-case it.
    email: z.string().trim().toLowerCase().pipe(z.string().email().max(254)),
    // No minimum beyond non-empty: a length rule on the *login* form only tells
    // an attacker what the policy is. Strength is enforced where accounts are
    // created, not where they are used.
    password: z.string().min(1).max(200),
});
export const adminProfileSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    displayName: z.string().nullable(),
    roles: z.array(userRoleSchema),
    lastLoginAt: z.string().nullable(),
});
export const adminSessionSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    /** Access-token lifetime in seconds, so the console can refresh ahead of expiry. */
    expiresIn: z.number().int(),
    tokenType: z.literal('Bearer'),
    admin: adminProfileSchema,
});
// --- Capabilities -----------------------------------------------------------
// Enforced server-side per route and mirrored in the UI, so a read-only
// operator is never shown a button that is going to 403.
/** May open the console at all. */
export function canAccessConsole(roles) {
    return roles.some((role) => role === 'admin' || role === 'support');
}
/** May perform any state-changing action. `support` is read-only. */
export function canMutate(roles) {
    return roles.includes('admin');
}
/**
 * May un-redact medical conditions and medications.
 *
 * Deliberately narrower than "can read a user": revealing is a separate,
 * audited decision, and `support` never gets it.
 */
export function canRevealHealth(roles) {
    return roles.includes('admin');
}
//# sourceMappingURL=auth.js.map